/**
 * useHighRiskRecords - Data fetching for high-risk records
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FilterConfig } from '../../../records/filter';
import type {
  CauseReportHighRisk,
  UseHighRiskRecordsReturn,
} from '../types';
import {
  highRiskKeys,
  CAUSE_REPORT_STALE_TIME,
  HIGH_RISK_RECORDS_LIMIT,
  HIGH_RISK_THRESHOLD,
} from '../constants';
import { fetchWithAuth } from '../../../../../utils/api';
import { mapFiltersToApiParams, transformRecord } from '../utils';
import type { ApiRecord } from '../utils';

interface HighRiskApiResponse {
  records: ApiRecord[];
  total_high_risk: number;
}

function transformHighRisk(data: HighRiskApiResponse): CauseReportHighRisk {
  return {
    records: data.records.map(transformRecord),
    totalHighRisk: data.total_high_risk,
  };
}

async function fetchHighRisk(
  datasetId: string,
  params: Record<string, string>
): Promise<HighRiskApiResponse> {
  const queryParams = { ...params };
  queryParams.limit = String(HIGH_RISK_RECORDS_LIMIT);
  queryParams.min_score = String(HIGH_RISK_THRESHOLD);

  const queryString = new URLSearchParams(queryParams).toString();

  return fetchWithAuth(
    `/v1/datasets/${datasetId}/cause-report/high-risk?${queryString}`
  );
}

export function useHighRiskRecords(
  datasetId: string,
  filters: FilterConfig
): UseHighRiskRecordsReturn {
  const filterParams = useMemo(() => mapFiltersToApiParams(filters), [filters]);

  const query = useQuery({
    queryKey: highRiskKeys.list(datasetId, filterParams),
    queryFn: () => fetchHighRisk(datasetId, filterParams),
    select: transformHighRisk,
    staleTime: CAUSE_REPORT_STALE_TIME,
    enabled: !!datasetId,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
